/**
 * Troca de modelo do bloco de oferta.
 *
 * Trocar de template guarda o estado do modelo que sai e restaura o do modelo
 * que entra (ou semeia os defaults, se for a primeira visita). Tudo isso produz
 * **um único objeto** — o chamador faz um `setFeature` só.
 *
 * Isso é deliberado: aplicar os campos em vários `setFeature` sequenciais dentro
 * de um `requestAnimationFrame` (como fazia o protótipo) perde o trabalho se o
 * componente desmontar entre os frames, e deixa o usuário com o modelo novo
 * exibindo o conteúdo do modelo anterior.
 */

import type { ActionButton } from '@/store/videoFeatures/types/overlay-features';
import type {
  ActionButtonModel,
  ActionButtonModelSnapshot,
  ActionButtonPrimarySnapshot,
  ActionButtonTemplate,
} from '@/store/videoFeatures/types/action-button-templates';
import { defaultTemplate, modelDefaults, modelSpec } from './presets';

/** Recorta do botão o que pertence ao modelo atual. */
function readPrimary(actionButton: Partial<ActionButton>): ActionButtonPrimarySnapshot {
  const style = actionButton.style || {};
  return {
    text: actionButton.text,
    url: actionButton.url,
    size: actionButton.size,
    backgroundColor: style.backgroundColor,
    textColor: style.textColor,
    hoverBackgroundColor: style.hoverBackgroundColor,
    hoverTextColor: style.hoverTextColor,
    borderRadius: style.borderRadius,
    shadow: style.shadow,
    pulse: style.pulse,
  };
}

/** Devolve o botão com o recorte de primário aplicado, preservando o resto. */
function writePrimary(
  actionButton: Partial<ActionButton>,
  primary: ActionButtonPrimarySnapshot | undefined,
): Partial<ActionButton> {
  if (!primary) return actionButton;

  return {
    ...actionButton,
    text: primary.text ?? actionButton.text,
    url: primary.url ?? actionButton.url,
    size: primary.size ?? actionButton.size,
    style: {
      ...(actionButton.style || {}),
      backgroundColor: primary.backgroundColor ?? actionButton.style?.backgroundColor,
      textColor: primary.textColor ?? actionButton.style?.textColor,
      hoverBackgroundColor:
        primary.hoverBackgroundColor ?? actionButton.style?.hoverBackgroundColor,
      hoverTextColor: primary.hoverTextColor ?? actionButton.style?.hoverTextColor,
      borderRadius: primary.borderRadius ?? actionButton.style?.borderRadius,
      shadow: primary.shadow ?? actionButton.style?.shadow,
      pulse: primary.pulse ?? actionButton.style?.pulse,
    },
  };
}

/** Recorta do template o que é conteúdo do modelo (tudo menos o índice). */
function readTemplateSnapshot(
  template: ActionButtonTemplate | undefined,
): Omit<ActionButtonTemplate, 'model' | 'modelData'> {
  const { model: _model, modelData: _modelData, ...rest } = template || {};
  return rest;
}

/**
 * Troca o modelo do bloco de oferta.
 *
 * @returns o `actionButton` inteiro já atualizado — grave com um único
 *   `setFeature('actionButton', ...)`.
 */
export function switchActionButtonModel(
  actionButton: Partial<ActionButton>,
  nextModel: ActionButtonModel,
): Partial<ActionButton> {
  const template = actionButton.template || {};
  const currentModel = template.model || 'default';

  if (currentModel === nextModel) return actionButton;

  // 1. Guarda o modelo que sai.
  const outgoing: ActionButtonModelSnapshot = {
    ...readTemplateSnapshot(template),
    primary: readPrimary(actionButton),
  };
  const modelData = { ...(template.modelData || {}), [currentModel]: outgoing };

  // 2. Restaura o modelo que entra, ou semeia os defaults na primeira visita.
  const incoming = modelData[nextModel] ?? modelDefaults(nextModel);
  const { primary, ...incomingTemplate } = incoming;

  // 3. `renderAs` acompanha o modelo, não a config antiga: um downsell salvo como
  //    link não pode reaparecer como link num modelo que desenha dois botões.
  const spec = modelSpec(nextModel);
  const secondary = incomingTemplate.secondary
    ? {
        ...incomingTemplate.secondary,
        ...(spec.secondaryAs ? { renderAs: spec.secondaryAs } : {}),
      }
    : incomingTemplate.secondary;

  const withPrimary = writePrimary(actionButton, primary);

  return {
    ...withPrimary,
    template: {
      ...incomingTemplate,
      secondary,
      model: nextModel,
      modelData,
    },
  };
}

/**
 * Hidrata o bloco de oferta vindo da API (`tb_video.actionButtonConfig`).
 *
 * Preserva o objeto salvo como está — não faz merge com defaults, senão texto
 * que o usuário apagou voltaria no próximo load. Só garante que `model` existe,
 * porque tudo depende dele para resolver o layout.
 */
export function hydrateTemplate(raw: unknown): ActionButtonTemplate {
  if (!raw || typeof raw !== 'object') return defaultTemplate();

  const template = raw as ActionButtonTemplate;
  return { ...template, model: template.model || 'default' };
}

/** Slots textuais que contam como conteúdo preenchido pelo usuário. */
const TEXT_SLOTS = [
  'urgency',
  'headline',
  'subheadline',
  'offerText',
  'strikethrough',
  'featured',
  'price',
  'priceNote',
] as const;

/**
 * O bloco de oferta tem conteúdo de verdade, ou é só o esqueleto que a
 * hidratação semeia?
 *
 * Serve para o save não gravar `actionButtonConfig` em botão que nunca passou
 * pelo seletor. Sem isso, abrir e salvar um vídeo antigo o moveria do caminho
 * legado do player para o dos templates — que tem outra tipografia e outra
 * geometria — restilizando o botão sem o usuário ter pedido nada.
 */
export function hasTemplateContent(template: ActionButtonTemplate | undefined): boolean {
  if (!template) return false;

  if (template.model && template.model !== 'default') return true;
  if (template.modelData && Object.keys(template.modelData).length > 0) return true;
  if (template.openInNewTab === false) return true;
  if (template.progressBar?.enabled) return true;
  if (template.secondary?.text) return true;

  return TEXT_SLOTS.some((slot) => !!template[slot]?.text);
}

/**
 * Garante que o bloco de oferta existe e tem modelo.
 * Usado na hidratação de botões que nunca passaram pelo seletor.
 */
export function ensureTemplate(actionButton: Partial<ActionButton>): ActionButtonTemplate {
  const template = actionButton.template;
  if (template?.model) return template;

  return { ...defaultTemplate(), ...template, model: 'default' };
}
